import clsx from 'clsx'
import { Trans } from '@lingui/react/macro'
import { useMutation } from '@tanstack/react-query'

import { publicTransport } from '@jotta/grpc-connect-client/transport'
import { mutate } from '@jotta/grpc-connect-client/util'
import type { Country } from '@jotta/grpc-connect-openapi/country'
import type { Language } from '@jotta/grpc-connect-openapi/language'
import type { SignupRequest } from '@jotta/grpc-connect-openapi/signup'
import { SignupRequestSchema } from '@jotta/grpc-connect-openapi/signup'
import { signup as signupDescriptor } from '@jotta/grpc-connect-openapi/signupQuery'
import { useForm } from '@jotta/hooks'
import { AppError } from '@jotta/types/AppError'
import type { PartialMessage } from '@jotta/types/compat'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { Form } from '@jotta/ui/Form'
import SvgCheckCircle from '@jotta/ui/SvgCheckCircle'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import type { EmailVerification, LoggedIn } from './CreateUser'
import { CTAButton } from './CTAButton'
import {
  EmailInUse,
  generateSignupToken,
  newsletterField,
  useAcceptedTermsField,
} from './signupForm'
import { TermsAndConditionsCheckbox } from './TermsAndConditionsCheckbox'

export function FederatedSignupConfirmation({
  customerGroupCode,
  federationToken,
  onSuccess,
  onCaptchaRequested,
  onEmailVerification,
  onCancel,
  language,
  country,
  className,
  ctaLabelText,
}: {
  customerGroupCode: string
  federationToken: string
  onSuccess: (loggedIn: LoggedIn) => void
  onCaptchaRequested: () => void
  onEmailVerification: (emailVerification: EmailVerification) => void
  onCancel: () => void
  language: Language
  country?: Country
  className?: string
  ctaLabelText?: string
}) {
  const { mutateAsync: signup, isPending } = useMutation({
    throwOnError: false,
    mutationFn: async (signupRequest: PartialMessage<SignupRequest>) => {
      return mutate(
        signupDescriptor,
        await generateSignupToken(
          SignupRequestSchema,
          signupRequest,
          (message, token) => (message.token = token),
        ),
        publicTransport,
      )
    },
    onSuccess: signupResponse => {
      const signupResult = signupResponse?.result
      switch (signupResult?.case) {
        case undefined:
        case 'signupNotSupported':
          throw new Error('invalid signup response')
        case 'success':
          onSuccess(signupResult.value)
          break
        case 'redirect':
          window.location.assign(signupResult.value.url)
          break
        case 'emailInUse':
          throw new AppError({
            view: <EmailInUse />,
          })
        case 'captcha':
          onCaptchaRequested()
          break
        case 'emailVerification':
          onEmailVerification(signupResult.value)
          break
        default:
          return exhaustiveGuard(signupResult)
      }
    },
  })

  const form = useForm({
    fields: {
      acceptedTerms: useAcceptedTermsField(),
      wantsNewsletter: newsletterField,
    },
    submit: ({ wantsNewsletter }) =>
      signup({
        customerGroupCode,
        language,
        country,
        wantsNewsletter,
        request: {
          case: 'federatedSignup',
          value: {
            token: federationToken,
          },
        },
      }),
  })

  const { acceptedTerms, wantsNewsletter } = form

  return (
    <Form
      className={clsx('my-2 flex flex-col gap-4', className)}
      form={form}
      header={
        <>
          <div className="alert alert-good mb-8 flex">
            <PlainBrandIcon
              icon={SvgCheckCircle}
              height={24}
              className="pr-4"
            />
            <Trans>Logged in</Trans>
          </div>
          <h2 className="h2">
            <Trans>Complete signup</Trans>
          </h2>
        </>
      }
    >
      <TermsAndConditionsCheckbox
        acceptedTerms={acceptedTerms}
        newsletter={wantsNewsletter}
      />
      <CTAButton type="submit" loading={isPending}>
        {ctaLabelText || <Trans>Sign up</Trans>}
      </CTAButton>
      <Button
        className="btn btn-outline"
        disabled={isPending}
        onClick={event => {
          event.preventDefault()
          onCancel()
        }}
      >
        <Trans>Cancel</Trans>
      </Button>
    </Form>
  )
}
